import { useCallback, useEffect, useMemo, useState } from "react";

import type { Map } from "@locators/components/shared/hooks/useFlotiqStores/useFlotiqStores.types";
import { getLatLngAndRadius } from "@locators/components/shared/utils";
import debounce from "lodash.debounce";

import { useConfig } from "@jti/app-context/useConfig";
import type { FlotiqStore } from "@jti/fe-common";
import { FlotiqApi } from "@jti/rest-api";

import { getTransformedHorecaMarkers } from "../utils";

const DEFAULT_NUMBER_OF_STORES = 1000;

type UseHoReCaStoresArgs = {
  storesLimit?: number;
};

export type UseHoReCaStoresReturn = {
  horecaStores: FlotiqStore[];
  activeStoreId: string;
  getHoReCaStores: (map: Map) => Promise<void> | void;
  handleSelectStore: (storeId: string) => void;
  getUserMarkerPosition: () => void;
  userMarkerPosition: { lat: number; lng: number } | undefined;
};

export const useHoReCaStores = (params?: UseHoReCaStoresArgs): UseHoReCaStoresReturn => {
  const { storesLimit = DEFAULT_NUMBER_OF_STORES } = params || {};
  const { FLOTIQ_API_URL, FLOTIQ_API_KEY } = useConfig();

  const [stores, setStores] = useState<FlotiqStore[]>([]);
  const [activeStoreId, setActiveStoreId] = useState<string>("");
  const [userMarkerPosition, setUserMarkerPosition] = useState<{ lat: number; lng: number }>();

  const flotiqApi = useMemo(() => new FlotiqApi(FLOTIQ_API_URL, FLOTIQ_API_KEY), [FLOTIQ_API_KEY, FLOTIQ_API_URL]);

  const handleLoadMarkersFromApi = useCallback(
    async (map: Map) => {
      const mapData = getLatLngAndRadius(map);

      if (!mapData) {
        return;
      }

      const { lat, lng, radius } = mapData;

      try {
        const response = await flotiqApi.getHoreca({ radius, lat, lng, horecaLimit: storesLimit });
        if (!response.data) {
          return;
        }

        const transformedMarkers = getTransformedHorecaMarkers(response.data);
        setStores(transformedMarkers);
      } catch (e) {
        console.error(e);
      }
    },
    [flotiqApi, storesLimit]
  );

  const debouncedLoadMarkersFromApi = useMemo(
    () => debounce(handleLoadMarkersFromApi, 300),
    [handleLoadMarkersFromApi]
  );

  useEffect(() => () => debouncedLoadMarkersFromApi.cancel(), [debouncedLoadMarkersFromApi]);

  const handleSelectStore = useCallback((storeId: string) => {
    setActiveStoreId(storeId);
  }, []);

  const getUserMarkerPosition = useCallback(() => {
    if (!navigator.geolocation) {
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserMarkerPosition({ lat: position.coords.latitude, lng: position.coords.longitude });
      },
      () => {
        console.info("Geolocation permission denied or unavailable.");
      }
    );
  }, []);

  return {
    horecaStores: stores,
    activeStoreId,
    getHoReCaStores: debouncedLoadMarkersFromApi,
    handleSelectStore,
    getUserMarkerPosition,
    userMarkerPosition,
  };
};
